/**
 * HANDLE-LOGIN
 * Save session and redirect after login
 */

import { instanceBackend, instanceQrcode, instanceReporting } from "@/App"
import axios from "axios"
import { store } from "@/index"
import cookie from "react-cookies"
import { accountFetchLite_AsSuperadmin, accountGet } from "@/redux/account.actions"
import { Account, AccountAdsOptions, AccountModules } from "@/redux/account.types"
import {
  userActivate
  // userEditAndUpdate
} from "@/redux/user.actions"
import { User } from "@/redux/user.types"
import { _authReporting } from "@/redux/_auth.actions"
import { sessionGet } from "@/redux/_session.actions"
import { INTERFACE_TYPE, Session, SESSION_LANGUAGES } from "@/redux/_session.types"
import i18n from "@/translate/i18n"
import { v4 as uuid } from "uuid"
import { filterGetObserverAttributes } from "@/redux/filter.actions"
import ceils from "@/assets/ceils.json"
import { subscriptionFetch, subscriptionGet } from "@/redux/subscription.actions"
import { actionPlanActivate, actionPlanFetchSelf } from "@/redux/action-plan.actions"
import { surveyActivate } from "@/redux/survey.actions"
import { observersSeeAllResults } from "@/utils/observer-scope-options.utils"

const handleLogin = async (
  response: any,
  interfaceType: INTERFACE_TYPE,
  connectedAsSupervisor: boolean,
  authLevel: number,
  searchParams: URLSearchParams,
  lg: null | string
) => {
  //Est-ce que un module est défini
  const module: any = searchParams.get("module")

  //If token provided
  if (response.token) {
    //Save access token
    cookie.save("idtreeToken", response.token, { path: "/" })
    instanceBackend.defaults.headers.common["idtreeToken"] = response.token
    instanceQrcode.defaults.headers.common["idtreeToken"] = response.token
    instanceReporting.defaults.headers.common["idtreeToken"] = response.token
    //Save refresh token
    if (response.refreshToken) {
      cookie.save("idtreeRefreshToken", response.refreshToken, { path: "/" })
    }
  } else {
    cookie.remove("firebaseToken", { path: "/" })
    cookie.remove("idtreeToken", { path: "/" })
    cookie.remove("idtreeRefreshToken", { path: "/" })
    axios.defaults.headers.common["firebaseToken"] = null
    axios.defaults.headers.common["idtreeToken"] = null
  }

  //Init account au démarrage
  const _session: Session = new Session()

  //Init user object
  let user: User = new User()

  //Handle response
  if (response.user) {
    //Init account
    const account = new Account(response.account)
    const superAccount = new Account(account.isSuperAccount ? account : response.superAccount)

    //Auth to other services
    if (interfaceType !== "SUPERVISOR" && account.features.survey) {
      const responseReporting: any = await store.dispatch(_authReporting())
      if (typeof responseReporting === "string" && responseReporting.split(".").length === 3) {
        cookie.save("reportingToken", responseReporting, { path: "/" })
        instanceReporting.defaults.headers.common["reportingToken"] = responseReporting
      } else {
        // Never keep another account's token or save an API error as a token.
        cookie.remove("reportingToken", { path: "/" })
        delete instanceReporting.defaults.headers.common["reportingToken"]
      }
    }

    //For superadmin seach account
    if (account.isSuperAccount) {
      interfaceType = "ADMIN_PARENT"
      const accounts: any = await store.dispatch(accountFetchLite_AsSuperadmin())
      store.dispatch(accountGet(accounts.error ? [] : accounts))
    }

    //Init new
    user = new User(response.user)

    if (user.connexionMode) {
      _session.connexionMode = user.connexionMode
    }

    //Update sessions params
    _session.qvCode = response.qvCode ? response.qvCode : null
    _session.accountAllowedTemplates = account.allowedTemplates
    _session.accountColors = account.colors
    _session.accountId = account.id
    _session.accountInvitations = account.invitations
    _session.accountIsChildren = account.superAccountId ? true : false
    _session.accountIsTrial = account.isTrial
    _session.accountOptions = account.options
    _session.accountTemplateRule = account.templateRule
    _session.accountName = account.name
    _session.accountLastSubscriptionEnd = account.superAccountId ? null : account.lastSubscriptionEnd
    _session.isPasswordRequired = account.isPasswordRequired
    _session.isSuperAccount = account.isSuperAccount
    _session.authLevel = authLevel
    _session.connectedAsSuperadmin =
      interfaceType === "ADMIN_PARENT" || account.superAccountId === store.getState()._session.superaccountId
    _session.connectedAsSupervisor = connectedAsSupervisor
    _session.email = user.email ? user.email : ""
    _session.interfaceType = interfaceType
    _session.language = user.language ? user.language : "en"
    _session.modules = account.modules
    _session.participationMin = account.participationMin
    _session.phone = user.phone ? user.phone : ""
    _session.supervisorId = connectedAsSupervisor ? user.id : ""
    _session.supervisorLevel = connectedAsSupervisor ? user.level : 0
    _session.userRole = user.role
    _session.userFirstname = user.firstname
    _session.userId = interfaceType === "SUPERVISOR" ? "" : user.id
    _session.userLastname = user.lastname
    _session.userInitialLanguage = user.language.toLowerCase()
    _session.userOptions = user.options
    _session.userExcludedFilters = user.excludedFilters
    _session.userPasswordProvided = response.asAdmin || interfaceType !== "ADMIN" ? true : user.passwordProvided

    //Update features according user profile
    const features = account.features
    if (!connectedAsSupervisor) {
      if (!user.role) features.survey = false
      if (!user.OrganizationId && !user.EstablishmentId && user.role !== "ADMIN") features.risks = false
    }
    _session.features = features

    //Update risks params
    //Chargement asynchrone organization and establishment (because it is not the same type of the store - RTK - it charges only this way)
    const { default: organizationStore } = await import("@/features/adm/entities/organization/model/organization.store")
    const { default: establishmentStore } =
      await import("@/features/adm/entities/establishment/model/establishment.store")

    if (user.Organization) {
      store.dispatch(organizationStore.actions.setActive(user.Organization))
      store.dispatch(establishmentStore.actions.setActive(user.Establishment))
      store.dispatch(establishmentStore.actions.setList(user.Organization.Establishments))
      _session.risksInterfaceType = user.risksInterfaceType
      _session.risksRole = user.risksRole
    } else {
      store.dispatch(organizationStore.actions.setActive(null))
      store.dispatch(establishmentStore.actions.setActive(null))
    }

    //Get current feature
    if (account.features.survey && (user.role || connectedAsSupervisor)) {
      _session.featureConnected = "survey"
    } else if (account.features.risks) {
      _session.featureConnected = "survey"
    }

    //Update superadmin account informations
    //If not superadmin interface keep existant informations
    _session.superaccountColor = superAccount.colors.brandPrimary
    _session.superaccountId = superAccount.id
    _session.superaccountImage = superAccount.options.image
    _session.superaccountName = superAccount.name

    //Load packs + subscriptions + remaining invitations
    if (
      _session.interfaceType !== "SUPERVISOR" &&
      _session.userRole === "ADMIN" &&
      (_session.connectedAsSuperadmin || !_session.superaccountId)
    ) {
      const subscriptions: any = await store.dispatch(subscriptionFetch())
      store.dispatch(subscriptionGet(!subscriptions.error ? subscriptions : []))
    }
  }

  //Save soure
  let source = searchParams.get("utm_source")
  const redirection = searchParams.get("redirection")
  if (!source) source = ""

  //If auth public
  //set ad options for session
  if (authLevel === 0) {
    _session.onboardingModule = Object.keys(new AccountModules()).indexOf(module) > -1 ? module : "survey"
    _session.accountId = uuid()
    _session.accountAdsOptions = new AccountAdsOptions({
      SOURCE: source,
      CAMPAIGNID: searchParams.get("campaignid"),
      ADGROUPID: searchParams.get("adgroupid"),
      KEYWORD: searchParams.get("keyword"),
      GCLID: searchParams.get("gclid"),
      TARGETID: searchParams.get("targetid"),
      TRIAL_SOURCE: searchParams.get("trial_source"),
      TRIAL_PAGE: searchParams.get("trial_page")
    })

    //Get language if not defined
    if (!response.user.language) {
      const language = lg ? lg : navigator.language.split("-")[0]
      if (SESSION_LANGUAGES.find((x) => x.id === language)) {
        _session.language = language
      } else {
        _session.language = "en"
      }
    }
  }

  //Update language for the account
  i18n.changeLanguage(_session.language)

  //MAGIC BACKEND
  //Update ceils
  const currentCeil: any = ceils.find((x) => x.id === _session.accountId)
  if (currentCeil) {
    _session.accountOptions.ceilsBirthDate = currentCeil.ceilsBirthDate
    _session.accountOptions.ceilsCompanyWelcomeDate = currentCeil.ceilsCompanyWelcomeDate
  }

  //Update store
  store.dispatch(sessionGet(_session))

  //Init active user (for action plan)
  store.dispatch(
    userActivate(
      new User({
        id: _session.userId,
        firstname: _session.userFirstname,
        lastname: _session.userLastname
      })
    )
  )

  //Go to home page
  if (interfaceType === "SUPERVISOR") {
    return "/supervisor/accounts"
  } else {
    if (authLevel === 0) {
      if (redirection === "onboarding") {
        return "/onboarding/intro/0"
      } else {
        if (module) {
          return "/onboarding/signup?module=" + module
        } else {
          return "/onboarding/signup"
        }
      }
    } else {
      //Format population observers
      store.dispatch(filterGetObserverAttributes(user.populationsObserver, observersSeeAllResults(_session)))

      //Get destination Page
      //If task load action plan and redirect to action plan page
      if (redirection === "tasks") {
        const actionPlans: any = await store.dispatch(actionPlanFetchSelf())
        if (!actionPlans.error) {
          if (actionPlans.length === 1) {
            store.dispatch(surveyActivate(actionPlans[0].Survey))
            store.dispatch(actionPlanActivate(actionPlans[0]))
            if (actionPlans[0].tasks.length > 0) {
              return "/tasks/edit"
            } else {
              return "/tasks/wizard"
            }
          } else {
            return "/tasks/self"
          }
        }
        return "/tasks/self"
      }
      //Else home page
      else {
        return _session.featuresList.includes("risks") && !_session.featuresList.includes("survey") ? "/duerp" : "/home"
      }
    }
  }
}

export default handleLogin
